import { FC, useEffect, useRef } from "react";
import { useAppDispatch } from "hooks/useAppDispatch";
import { logoutThunk } from "reduxStore/auth/thunks";
import {
  clearSessionActivity,
  getIdleTimeoutMs,
  isIdleExpired,
  touchSessionActivity,
} from "utils/sessionIdle";

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
];

/**
 * Signs out after REACT_APP_IDLE_TIMEOUT_MINUTES of no interaction (default 30).
 * Mount only inside authenticated layouts.
 */
export const SessionIdleGuard: FC = () => {
  const dispatch = useAppDispatch();
  const timeoutMs = getIdleTimeoutMs();
  const loggingOut = useRef(false);

  useEffect(() => {
    if (timeoutMs <= 0) return undefined;

    touchSessionActivity();
    loggingOut.current = false;

    const onActivity = () => {
      if (document.visibilityState === "hidden") return;
      touchSessionActivity();
    };

    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, onActivity, { passive: true });
    }
    // Returning to a tab counts as activity; it lives on document, not window.
    document.addEventListener("visibilitychange", onActivity);

    const intervalId = window.setInterval(() => {
      if (loggingOut.current) return;
      if (!isIdleExpired(timeoutMs)) return;
      loggingOut.current = true;
      clearSessionActivity();
      void dispatch(logoutThunk()).finally(() => {
        window.location.assign("/");
      });
    }, 15_000);

    return () => {
      window.clearInterval(intervalId);
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, onActivity);
      }
      document.removeEventListener("visibilitychange", onActivity);
    };
  }, [dispatch, timeoutMs]);

  return null;
};
